import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppTranslateService } from 'src/app/services/translate.service';
import { User } from '../../../entities/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { first, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AnalyticsService } from 'src/app/services/gtag.service';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule, FormsModule, TranslateModule],
  templateUrl: './signup.page.html',
  styleUrls: ['./signup.page.scss'],
})
export class SignUpPage implements OnInit {
  email = '';
  password = '';
  confirmPassword = '';
  displayName = '';
  errorMessage = '';
  user: User | null = null;
  temporary: boolean = false;
  public isNative: boolean = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform && Capacitor.isNativePlatform();

  constructor(private authService: AuthService, private router: Router, private translateService: AppTranslateService, private translate: TranslateService, private gtagService: AnalyticsService) {}

  ngOnInit() {
    // Debug snapshot to see native vs JS auth on entry
    void this.authService.debugAuthSnapshot('signup-init');

    // Wait for auth state to fully resolve before deciding navigation
    this.authService.authStateResolved$
      .pipe(first((resolved) => resolved === true))
      .pipe(switchMap(() => this.authService.currentUser$.pipe(first())))
      .subscribe((user) => {
        this.user = user as any;
        console.log('[SignUpPage] auth resolved; currentUser:', this.user);
        if (this.user) {
          // Check if user is anonymous (no email)
          if (!this.user.email) {
            this.temporary = true;
            console.log('[SignUpPage] anonymous user -> stay on signup');
            return; // Stay on signup page for anonymous users
          }
        } else {
          console.log('[SignUpPage] no user -> stay on signup');
        }
      });
  }

  async signUp() {
    // Clear any previous error messages
    this.errorMessage = '';
    
    // Client-side validation
    if (!this.email) {
      this.errorMessage = this.translate.instant('SIGNUP.ERROR_EMAIL_REQUIRED');
      return;
    }

    if (!this.isValidEmail(this.email)) {
      this.errorMessage = this.translate.instant('SIGNUP.ERROR_EMAIL_INVALID');
      return;
    }

    if (!this.password || this.password.length < 6) {
      this.errorMessage = this.translate.instant('SIGNUP.ERROR_PASSWORD_WEAK');
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.errorMessage = this.translate.instant('SIGNUP.ERROR_PASSWORD_MISMATCH');
      return;
    }

    try {
      if (this.user && !this.user.email) {
        // User is anonymous, convert to email account
        const firebaseUser = await this.authService.convertAnonymousToEmail(this.email, this.password, this.displayName);
        if (firebaseUser) {
          this.gtagService.trackEvent('signup', 'email_convert', 'success');
          // Set user ID for better analytics tracking
          await this.gtagService.setUserId(firebaseUser.uid);
          this.router.navigate(['/news']);
        }
      } else {
        // Regular sign up
        const firebaseUser = await this.authService.signUpWithEmail(this.email, this.password, this.displayName, this.translateService.nativeLanguage);
        if (firebaseUser) {
          this.gtagService.trackEvent('signup', 'email', 'success');
          // Set user ID for better analytics tracking
          await this.gtagService.setUserId(firebaseUser.uid);
          this.router.navigate(['/beginning']);
        }
      }
    } catch (error: any) {
      console.error('Sign-up error:', error);
      if (error.code === 'auth/email-already-in-use') {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_EMAIL_IN_USE');
      } else if (error.code === 'auth/weak-password') {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_WEAK_PASSWORD');
      } else if (error.code === 'auth/credential-already-in-use') {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_CREDENTIAL_IN_USE');
      } else {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_GENERAL');
      }
    }
  }

  async signUpWithGoogle() {
    try {
      if (this.user && !this.user.email) {
        // User is anonymous, convert to Google account
        const firebaseUser = await this.authService.convertAnonymousToGoogle();
        if (firebaseUser) {
          this.gtagService.trackEvent('signup', 'google_convert', 'success');
          // Set user ID for better analytics tracking
          await this.gtagService.setUserId(firebaseUser.uid);
          this.router.navigate(['/news']);
        }
      } else {
        // Regular Google sign up
        const firebaseUser = await this.authService.signInWithGoogle(this.translateService.nativeLanguage);
        if (firebaseUser) {
          this.gtagService.trackEvent('signup', 'google', 'success');
          // Set user ID for better analytics tracking
          await this.gtagService.setUserId(firebaseUser.uid);
          this.router.navigate(['/beginning']);
        }
      }
    } catch (error: any) {
      console.error('Google sign-up error:', error);
      if (error.code === 'auth/credential-already-in-use') {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_GOOGLE_ALREADY_LINKED');
      } else {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_GOOGLE_FAILED');
      }
    }
  }

  async signUpWithApple() {
    try {
      if (this.user && !this.user.email) {
        // User is anonymous, convert to Apple account
        const firebaseUser = await this.authService.convertAnonymousToApple();
        if (firebaseUser) {
          this.gtagService.trackEvent('signup', 'apple_convert', 'success');
          // Set user ID for better analytics tracking
          await this.gtagService.setUserId(firebaseUser.uid);
          this.router.navigate(['/news']);
        }
      } else {
        // Regular Apple sign up
        const result = await this.authService.signInWithApple(this.translateService.nativeLanguage);
        if (result) {
          const { user: firebaseUser, isNewUser } = result;
          this.gtagService.trackEvent('signup', 'apple', 'success');
          // Set user ID for better analytics tracking
          await this.gtagService.setUserId(firebaseUser.uid);
          this.router.navigate(['/beginning']);
        }
      }
    } catch (error: any) {
      console.error('Apple sign-up error:', error);
      if (error.code === 'auth/credential-already-in-use') {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_APPLE_ALREADY_LINKED');
      } else {
        this.errorMessage = this.translate.instant('SIGNUP.ERROR_APPLE_FAILED');
      }
    }
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

  anonSignUp() {
    this.router.navigate(['/beginning']);
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}